import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface UserSettings {
  id: string;
  user_id: string;
  low_hours_threshold: number;
  show_low_hours_warning: boolean;
  show_active_timer_alert: boolean;
  show_duplicate_warning: boolean;
  dashboard_assignment_sort: string;
  assignment_manager_sort: string;
  created_at: string;
  updated_at: string;
}

const DEFAULT_SETTINGS = {
  low_hours_threshold: 5,
  show_low_hours_warning: true,
  show_active_timer_alert: true,
  show_duplicate_warning: true,
  dashboard_assignment_sort: 'date-desc',
  assignment_manager_sort: 'date-desc'
};

export const useUserSettings = (userId: string | null) => {
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    loadSettings();
  }, [userId]);

  const loadSettings = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings(data);
      } else {
        await createDefaultSettings();
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultSettings = async () => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('user_settings')
        .insert({
          user_id: userId,
          ...DEFAULT_SETTINGS
        })
        .select()
        .single();

      if (error) throw error;
      setSettings(data);
    } catch (error) {
      console.error('Error creating default settings:', error);
    }
  };

  const updateSettings = async (updates: Partial<UserSettings>) => {
    if (!userId || !settings) return;

    try {
      const { data, error } = await supabase
        .from('user_settings')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      if (data) setSettings(data);
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  return {
    settings: settings || {
      id: '',
      user_id: userId || '',
      ...DEFAULT_SETTINGS,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    loading,
    refetch: loadSettings,
    updateSettings
  };
};
